import React, { useEffect, useMemo, useState } from "react";
import { useRouter } from "next/router";
import BookmarkBorderIcon from "@mui/icons-material/BookmarkBorder";
import BookmarkIcon from "@mui/icons-material/Bookmark";
import { Button, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions } from "@mui/material";
import { useAuth } from "@/context/AuthContext";
import { openAccessGateModal } from "@/utils/accessGate";
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
const normalizeKey = value => {
  if (!value) return null;
  const trimmed = value.trim();
  if (!trimmed) return null;
  return trimmed.toLowerCase();
};

/**
 * Small reusable button that lets an authenticated user toggle saved status for the current page.
 */
const SavedToggleButton = ({
  resourceType,
  resourceId,
  resourceSlug,
  resourceTitle,
  resourceSummary,
  resourceImage,
  meta,
  loginRedirect,
  label,
  confirmSave = false,
  onSavedChange,
  variant = "outlined",
  size = "small",
  ...buttonProps
}) => {
  const {
    isAuthenticated,
    initializing,
    savedItems,
    addSavedItem,
    removeSavedItem
  } = useAuth();
  const router = useRouter();
  const [toggling, setToggling] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const normalizedId = useMemo(() => normalizeKey(resourceId), [resourceId]);
  const normalizedSlug = useMemo(() => normalizeKey(resourceSlug), [resourceSlug]);

  // Derive saved state from context's savedItems list
  const isSaved = useMemo(() => {
    if (!isAuthenticated || !normalizedId) return false;
    return savedItems.some(item => item.resourceType === resourceType && (item.resourceId === normalizedId || normalizedSlug && item.resourceSlug === normalizedSlug));
  }, [isAuthenticated, normalizedId, normalizedSlug, resourceType, savedItems]);

  // Notify parent when derived saved state changes
  const [prevSaved, setPrevSaved] = useState(isSaved);
  useEffect(() => {
    if (isSaved !== prevSaved) {
      setPrevSaved(isSaved);
      onSavedChange?.(isSaved);
    }
  }, [isSaved, prevSaved, onSavedChange]);
  const handleClick = async () => {
    if (toggling) return;
    if (!normalizedId) return;
    if (!isAuthenticated) {
      const target = loginRedirect ?? router.asPath ?? "/";
      openAccessGateModal({
        reason: "login-required",
        returnUrl: target
      });
      return;
    }

    // If confirmSave is on and we're about to save, show dialog first
    if (confirmSave && !isSaved) {
      setConfirmOpen(true);
      return;
    }
    await executeToggle();
  };
  const executeToggle = async () => {
    if (toggling || !normalizedId) return;
    try {
      setToggling(true);
      if (isSaved) {
        await removeSavedItem(resourceType, normalizedId);
      } else {
        await addSavedItem({
          resourceType,
          resourceId: normalizedId,
          resourceSlug: normalizedSlug,
          resourceTitle: resourceTitle ?? null,
          resourceSummary: resourceSummary ?? null,
          resourceImage: resourceImage ?? null,
          meta: meta ?? null
        });
      }
    } catch (error) {
      if (process.env.NODE_ENV !== "production") {
        // eslint-disable-next-line no-console
        console.error("[SavedToggleButton] Failed to toggle saved state", error);
      }
    } finally {
      setToggling(false);
    }
  };
  const {
    disabled: disabledProp,
    ...restButtonProps
  } = buttonProps;
  const disabled = initializing || toggling || !normalizedId || Boolean(disabledProp);
  const defaultLabel = isSaved ? label?.saved ?? "Saved" : label?.save ?? "Save";
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx(Button, {
      variant: variant,
      size: size,
      startIcon: isSaved ? /*#__PURE__*/_jsx(BookmarkIcon, {}) : /*#__PURE__*/_jsx(BookmarkBorderIcon, {}),
      onClick: handleClick,
      disabled: disabled,
      "aria-pressed": isSaved,
      ...restButtonProps,
      children: defaultLabel
    }), confirmSave && /*#__PURE__*/_jsxs(Dialog, {
      open: confirmOpen,
      onClose: () => setConfirmOpen(false),
      onKeyDown: e => {
        if (e.key === "Enter") {
          e.preventDefault();
          setConfirmOpen(false);
          void executeToggle();
        }
      },
      children: [/*#__PURE__*/_jsxs(DialogTitle, {
        children: ["Save", " ", resourceType.charAt(0).toUpperCase() + resourceType.slice(1)]
      }), /*#__PURE__*/_jsx(DialogContent, {
        children: /*#__PURE__*/_jsxs(DialogContentText, {
          children: ["Save", resourceTitle ? ` "${resourceTitle}"` : "", " to your saved ", resourceType, "s?"]
        })
      }), /*#__PURE__*/_jsxs(DialogActions, {
        children: [/*#__PURE__*/_jsx(Button, {
          onClick: () => setConfirmOpen(false),
          children: "Cancel"
        }), /*#__PURE__*/_jsx(Button, {
          variant: "contained",
          disabled: toggling,
          onClick: () => {
            setConfirmOpen(false);
            void executeToggle();
          },
          children: toggling ? "Saving…" : "Save"
        })]
      })]
    })]
  });
};
export default SavedToggleButton;